import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { LogOut, User, X } from 'lucide-react'
import { RootState, AppDispatch } from '../../app/store'
import { logout } from '../../features/auth/authSlice'
import apiService from '../../services/api'
import toast from 'react-hot-toast'
import { isCustomerServiceUser } from '../../utils/apiUtils'
import { scheduleFullRefresh } from '../../utils/sessionHelpers'
import homeIcon from '../../assets/images/home_color.svg'
import orderIcon from '../../assets/images/order.svg'
import moneyIcon from '../../assets/images/mos.svg'
import fallbackLogo from '../../assets/images/logo.svg'
import { getCurrentDesign } from '@/services/settingsService'

interface SideNavProps {
  isOpen: boolean
  onClose: () => void
  onCloseShift?: () => void
}

type NavItemKey = 'home' | 'orders' | 'collection' | 'profile'

type NavItem = {
  key: NavItemKey
  label: string
  path: string
  icon:
    | {
        type: 'image'
        src: string
        alt: string
      }
    | {
        type: 'component'
        Component: React.ComponentType<React.SVGProps<SVGSVGElement>>
      }
}

const SideNav: React.FC<SideNavProps> = ({
  isOpen,
  onClose,
  onCloseShift
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const { settings } = useSelector((state: RootState) => state.settings)
  const location = useLocation()
  const isRTL = settings.language === 'ar'
  const cleanedPath = location.pathname.replace(/^\/+/, '')
  const firstSegment = cleanedPath.split('/')[0]
  const currentPage: NavItemKey =
    firstSegment === 'orders'
      ? 'orders'
      : firstSegment === 'collection'
        ? 'collection'
        : firstSegment === 'profile'
          ? 'profile'
          : 'home'
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false)
  const isCS = isCustomerServiceUser()
  const [headerLogoUrl, setHeaderLogoUrl] = useState<string | undefined>(() => getCurrentDesign().headerLogo)

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent).detail as { headerLogo?: string } | undefined
      if (detail?.headerLogo) {
        setHeaderLogoUrl(detail.headerLogo)
      }
    }
    window.addEventListener('designSettingsApplied', handler as (event: Event) => void)
    return () => {
      window.removeEventListener('designSettingsApplied', handler as (event: Event) => void)
    }
  }, [])

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  const handleLogoutClick = () => {
    onClose()
    setShowLogoutConfirm(true)
  }

  const handleLogoutConfirm = async () => {
    if (isLoggingOut) return
    
    setShowLogoutConfirm(false)
    setIsLoggingOut(true)
    try {
      await apiService.post('/cashier/auth/logout')
      
      const hasActiveShift = localStorage.getItem('has_active_shift')
      const lastShiftCheck = localStorage.getItem('last_shift_check')
      
      dispatch(logout())
      
      if (hasActiveShift) {
        localStorage.setItem('has_active_shift', hasActiveShift)
      }
      if (lastShiftCheck) {
        localStorage.setItem('last_shift_check', lastShiftCheck)
      }
      
      toast.success(t('auth.logout_success') || 'تم تسجيل الخروج بنجاح')
      onClose()
      navigate('/login', { replace: true })
      scheduleFullRefresh()
    } catch (error: any) {
      const hasActiveShift = localStorage.getItem('has_active_shift')
      const lastShiftCheck = localStorage.getItem('last_shift_check')
      
      dispatch(logout())
      
      if (hasActiveShift) {
        localStorage.setItem('has_active_shift', hasActiveShift)
      }
      if (lastShiftCheck) {
        localStorage.setItem('last_shift_check', lastShiftCheck)
      }
      
      onClose()
      navigate('/login', { replace: true })
      scheduleFullRefresh()
    } finally {
      setIsLoggingOut(false)
    }
  }

  const handleLogoutCancel = () => {
    setShowLogoutConfirm(false)
  }

  const handleNavigation = (path: string) => {
    navigate(path)
    onClose()
  }

  const handleCloseShift = () => {
    if (onCloseShift) {
      onCloseShift()
    }
    onClose()
  }

  const navItems: NavItem[] = [
    {
      key: 'home',
      label: t('home.home'),
      path: '/home',
      icon: { type: 'image', src: homeIcon, alt: t('home.home') }
    },
    {
      key: 'orders',
      label: t('home.orders'),
      path: '/orders',
      icon: { type: 'image', src: orderIcon, alt: t('home.orders') }
    },
    {
      key: 'collection',
      label: t('home.collection') || 'التحصيل',
      path: '/collection',
      icon: { type: 'image', src: moneyIcon, alt: t('home.collection') || 'التحصيل' }
    },
    {
      key: 'profile',
      label: t('navigation.profile'),
      path: '/profile',
      icon: { type: 'component', Component: User }
    }
  ]

  const slideClass = isOpen
    ? 'translate-x-0'
    : isRTL
      ? 'translate-x-full'
      : '-translate-x-full'
  const borderRadiusClass = isRTL ? 'rounded-tl-2xl rounded-bl-2xl' : 'rounded-tr-2xl rounded-br-2xl'

  const renderNavIcon = (item: NavItem, active: boolean) => {
  if (item.icon.type === 'image') {
    return (
      <img
        src={item.icon.src}
        alt={item.icon.alt}
        className={`h-5 w-5 brightness-0 ${active ? 'invert' : 'opacity-40'}`}
      />
    )
  }

    const IconComponent = item.icon.Component
  return <IconComponent className={`h-5 w-5 ${active ? 'text-white' : 'text-[#818181]'}`} />
  }

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 transition-opacity duration-200"
          onClick={onClose}
        />
      )}
      <div
        className={`
          fixed inset-y-0 
          z-[100] w-72 bg-white ${borderRadiusClass}
          transition-transform duration-200 ease-linear
          ${slideClass}
        `}
        style={{ height: '100vh', overflow: 'hidden' }}
      >
        <div className="flex h-full flex-col">
          <div className="px-4 pt-4">
            <div className="flex items-center justify-start gap-4">
            <button
                type="button"
                onClick={onClose}
                aria-label={t('common.close')}
                className="p-2 rounded-xl bg-white border border-[#E1E4EA] shadow-[0px_1px_2px_rgba(18,43,105,0.08)] hover:bg-[#F4F5F7] transition-colors"
              >
                <X className="h-4 w-4 text-flutter-button" />
              </button>
              <div className="flex items-center gap-3">
                <img
                  src={headerLogoUrl || fallbackLogo}
                  alt="Brand logo"
                  className="h-12 w-auto object-contain"
                />
              </div>
              
            </div>
          </div>

          <div className="flex flex-1 flex-col px-4 pb-6 overflow-y-auto">
            <div className="mt-6 flex flex-col gap-3">
              {navItems.map((item) => {
                const isActive = currentPage === item.key
              const buttonClasses = `
                self-stretch h-10 px-4 rounded-[40px]
                flex items-center gap-1.5
                text-[14px] font-semibold leading-5 transition duration-200
                ${isActive ? 'bg-flutter-button text-white shadow-[0px_0px_0px_1px_rgba(0,0,0,0.25)]' : 'bg-gray-100 text-[#818181] hover:bg-[#F2F2F7]'}
              `
                return (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => handleNavigation(item.path)}
                    className={buttonClasses}
                  >
                    {renderNavIcon(item, isActive)}
                    <span className={`font-['IBM Plex Sans Arabic']`}>
                      {item.label}
                    </span>
                  </button>
                )
              })}
            </div>

            <div className="mt-auto flex flex-col gap-3">
              {!isCS && (
                <button
                  type="button"
                  onClick={handleCloseShift}
                  className={`

                    self-stretch h-10 px-4 rounded-[40px]
                    flex items-center gap-1.5
                    text-sm font-semibold leading-5
                    bg-gray-100 text-[#525866] hover:bg-[#F2F2F7]
                  `}
                >
                  <img
                    src={moneyIcon}
                    alt={t('home.close_session')}
                    className="h-5 w-5 brightness-0 opacity-40"
                  />
                  <span className={`font-['IBM Plex Sans Arabic'] `}>
                    {t('home.close_session')}
                  </span>
                </button>
              )}

              <button
                type="button"
                onClick={handleLogoutClick}
                disabled={isLoggingOut}
                className={`
                  self-stretch h-10 px-4 rounded-[40px]
                  flex items-center gap-1.5
                  text-sm font-semibold leading-5
                  bg-gray-100
                  bg-background text-[#525866]
                  hover:bg-[#F2F2F7]
                  disabled:opacity-50 disabled:cursor-not-allowed
                `}
              >
                <LogOut className="h-5 w-5" />
                <span className={`font-['IBM Plex Sans Arabic'] `}>
                  {t('auth.logout')}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {showLogoutConfirm && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
            onClick={handleLogoutCancel}
          />
          <div className={`relative w-full max-w-md rounded-3xl bg-white/95 backdrop-blur-md shadow-[0px_24px_48px_rgba(18,18,23,0.08)] p-8 space-y-6 ${isRTL ? 'text-right' : 'text-left'}`}>
            <div className="flex flex-col items-center gap-2">
              <h3 className={`text-2xl font-medium text-neutral-900 text-center font-['IBM Plex Sans Arabic']`}>
                {t('auth.confirm_logout_title')}
              </h3>
              <p className={`text-base text-black text-center leading-6 font-['IBM Plex Sans Arabic']`}>
                {t('auth.confirm_logout_message')}
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <button
                onClick={handleLogoutConfirm}
                disabled={isLoggingOut}
                className="w-full h-12 rounded-xl bg-flutter-button text-white font-semibold flex items-center justify-center gap-2 shadow-[inset_0_0_0_1px_rgba(0,0,0,0.15)] transition-colors hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <LogOut className="h-4 w-4" />
                <span className="font-['IBM Plex Sans Arabic']">
                  {t('auth.confirm_logout_action')}
                </span>
              </button>
              <button
                onClick={handleLogoutCancel}
                disabled={isLoggingOut}
                className="h-12 rounded-xl border border-gray-300 bg-white text-black font-semibold flex items-center justify-center font-['IBM Plex Sans Arabic'] hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default SideNav

